
import { useEffect } from 'react';

export default function ContactFormSection() {
  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on component unmount
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <section className="py-12 sm:py-20 bg-dark-800">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          {/* Calendly Widget - Now on the left */}
          <div className="space-y-6 sm:space-y-8 order-2 lg:order-1">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Schedule Your Consultation</h2>
              <p className="text-gray-300 mb-6 sm:mb-8 text-sm sm:text-base">Book a convenient time to discuss your mortgage needs in detail.</p>
              
              {/* Calendly Widget Container */}
              <div className="bg-dark-900 border border-dark-700 rounded-2xl p-2 sm:p-4 shadow-xl">
                <div 
                  className="calendly-inline-widget" 
                  data-url="https://calendly.com/tjcmonteiro/free-mortgage-consultation-with-tony-monteiro"
                  style={{
                    minWidth: '280px',
                    height: '600px',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}
                ></div>
              </div>
            </div>
          </div>
          
          {/* Why Choose Me - Now on the right */}
          <div className="space-y-6 sm:space-y-8 order-1 lg:order-2">
            <div className="space-y-4 sm:space-y-6">
              <h3 className="text-2xl sm:text-3xl font-bold text-white">Why Work With Me?</h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h5 className="font-bold text-base sm:text-lg text-white mb-1 sm:mb-2">Licensed & Certified</h5>
                    <p className="text-gray-300 text-xs sm:text-sm">Level 2 Certified Mortgage Professional with License #13121. You're working with a fully qualified expert.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h5 className="font-bold text-base sm:text-lg text-white mb-1 sm:mb-2">25+ Years Experience</h5>
                    <p className="text-gray-300 text-xs sm:text-sm">Over two decades helping Ontario families secure the right mortgage solutions, even in complex situations.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-key-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h5 className="font-bold text-base sm:text-lg text-white mb-1 sm:mb-2">Private Lending Access</h5>
                    <p className="text-gray-300 text-xs sm:text-sm">When traditional banks say no, I have alternative lending solutions to help you achieve your goals.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h5 className="font-bold text-base sm:text-lg text-white mb-1 sm:mb-2">Personal Service</h5>
                    <p className="text-gray-300 text-xs sm:text-sm">Direct access to me - no call centers or middlemen. You'll always know exactly what's happening with your application.</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Testimonial */}
            <div className="bg-dark-900 border border-dark-700 rounded-xl p-4 sm:p-6">
              <div className="flex items-center mb-3 sm:mb-4">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm sm:text-base"></i>
                  ))}
                </div>
              </div>
              <blockquote className="text-gray-300 italic mb-3 sm:mb-4 text-sm sm:text-base">
                "Tony found us a solution when three other brokers couldn't help. His private lending connections saved our home purchase. Professional, knowledgeable, and truly cares about his clients."
              </blockquote>
              <cite className="text-white font-semibold text-sm sm:text-base">- Sarah & Mike T., London ON</cite>
            </div>

            {/* Contact Info Card */}
            <div className="bg-dark-900 border border-dark-700 rounded-xl p-4 sm:p-6">
              <h4 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">Prefer to Call?</h4>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <i className="ri-phone-line text-red-500 text-lg sm:text-xl"></i>
                  <a href="tel:5196583026" className="text-red-500 font-semibold hover:text-red-400 transition-colors text-sm sm:text-base">
                    (519) 658-3026
                  </a>
                </div>
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <i className="ri-mail-line text-red-500 text-lg sm:text-xl"></i>
                  <a href="mailto:tmonteiro@mortgagealliance.com" className="text-red-500 font-semibold hover:text-red-400 transition-colors text-sm sm:text-base break-all">
                    tmonteiro@mortgagealliance.com
                  </a>
                </div>
                <p className="text-gray-400 text-xs sm:text-sm mt-3 sm:mt-4">Available Mon-Fri 9AM-7PM for immediate assistance</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
